<!-- Shows all process tasks that reference this business entity across all processes. -->
<!-- Mirrors the RoleResponsibilities pattern: fetched on mount, links to the task page. -->
<template>
    <div class="entity-task-refs-section">
        <Title>Referenced by Tasks</Title>
        <p v-if="!taskRefs?.length && !isLoading" class="no-refs">
            No tasks reference this entity yet. Assign this entity to a process
            task to see it here.
        </p>
        <DataTable
            v-else
            ref="taskRefsTable"
            :value="taskRefs"
            :loading="isLoading"
            class="task-refs-table"
            selectionMode="single"
            @row-click="
                (event: DataTableRowClickEvent) => {
                    const linkId = `task-ref-link-${event.data.task_id}`;
                    taskRefsTable.$el.querySelector(`#${linkId}`)?.click();
                }
            "
        >
            <Column field="task_name" header="Task">
                <template #body="{ data }">
                    <RouterLink
                        :id="`task-ref-link-${data.task_id}`"
                        class="task-link"
                        :to="{
                            name: 'processElementPage',
                            params: {
                                orgName: route.params.orgName,
                                stekName: route.params.stekName,
                                elementId: data.task_id,
                                elementType: 'task',
                            },
                        }"
                    >
                        {{ data.task_name }}
                    </RouterLink>
                </template>
            </Column>
            <Column field="label" header="Relationship">
                <template #body="{ data }">
                    <span class="relationship-label">{{ data.label }}</span>
                </template>
            </Column>
            <Column field="process_name" header="Process">
                <template #body="{ data }">
                    <span class="process-name">{{ data.process_name }}</span>
                </template>
            </Column>
        </DataTable>
    </div>
    <template v-for="ref in taskRefs ?? []" :key="ref.task_id">
        <Popup
            v-if="!isEditing && ref.task_id"
            :anchor="`a#task-ref-link-${ref.task_id}`"
        >
            <GenericCard
                :entityId="ref.task_id"
                :entityType="EntityType.TASK"
                :showPath="true"
            />
        </Popup>
    </template>
</template>

<script setup lang="ts">
import Column from "primevue/column";
import type { DataTableRowClickEvent } from "primevue/datatable";
import DataTable from "primevue/datatable";
import { ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";

import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Popup from "@/components/Popup.vue";
import { useEditManager } from "@/composables/edit-manager";
import { api, type types } from "@/lib/openapi";
import { useBusinessEntityManager } from "@/stores/business-entity-store";
import { EntityType } from "@/stores/entity-store";

import Title from "../Title.vue";

const route = useRoute();
const taskRefsTable = ref();
const { businessEntity } = useBusinessEntityManager();
const { isEditing } = useEditManager();

const taskRefs = ref<types.EntityTaskRef[] | undefined>(undefined);
const isLoading = ref(false);

const fetchTaskRefs = async (entityId: string) => {
    isLoading.value = true;
    taskRefs.value = undefined;
    try {
        const { data, response } =
            await api.startProcessProcessElementMgtGetProcessElementsByEntityStartPost(
                { body: { entity_id: entityId } }
            );
        if (response.ok) {
            taskRefs.value = data;
        }
    } finally {
        isLoading.value = false;
    }
};

watch(
    () => businessEntity.value?.identity,
    (id) => {
        if (id) fetchTaskRefs(id);
        else taskRefs.value = undefined;
    },
    { immediate: true }
);
</script>

<style scoped lang="scss">
.entity-task-refs-section {
    margin-top: 1rem;
}

.no-refs {
    color: var(--text-color-secondary);
    font-style: italic;
    margin: 1rem 0;
}

.task-refs-table {
    margin: 1rem 0;
}

.task-link {
    text-decoration: none;
    color: var(--stekz-primary-color);
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }
}

.process-name {
    color: var(--text-color-secondary);
}

.relationship-label {
    font-style: italic;
    color: var(--text-color-secondary);
}
</style>
